import { useCallback, useEffect, useState } from 'react';
import { Col, Row, Timeline } from 'antd';
import styles from './index.module.scss';
import { ReactComponent as CloseIcon } from '../../svgs/closeIcon.svg';
import { InfoSlider } from '../InfoSlider/InfoSlider';
import errorHandler from 'shared/utils/errorHandler';
import { apiD2r } from '../../api';
import { Loading } from '../Loading';
import { PaymentForm } from './PaymentForm';
import { SalesmanSelectForm } from './SalesmanSelectForm';
import { sortByDate } from 'shared/utils/date';
import { PaymentLogs } from './PaymentLogs';

interface ID2RInvoiceSlider {
	orderId: string;
	onClose: () => void;
	salesmanId?: string;
	onPaymentCaptured?: () => void;
}

export interface IPaymentLogs {
	createdAt: string;
	remarks: string;
	proofUrl?: string;
}
interface Logs extends IPaymentLogs {
	length: number;
}

interface IInvoice {
	billLocation: string;
	invoiceId: string;
	billAmount: number;
	amountPaid: number;
	isClosed: boolean;
}

export function D2RInvoiceSlider(props: Readonly<ID2RInvoiceSlider>) {
	const { orderId, onClose, salesmanId, onPaymentCaptured } = props;
	const [loading, setLoading] = useState(false);
	const [logs, setLogs] = useState<Logs[]>([]);
	const [invoice, setInvoice] = useState<IInvoice>({
		billLocation: '',
		invoiceId: '',
		billAmount: 0,
		amountPaid: 0,
		isClosed: false
	});

	//to override the internal antd http calls
	const dummyReq = (options: { onSuccess: () => void }) => {
		const { onSuccess } = options;
		onSuccess();
	};

	const getPaymentLogs = useCallback(async () => {
		try {
			setLoading(true);
			const response = await apiD2r.get(`/collections/${orderId}`);
			if (response?.data?.status) {
				setLogs(response?.data?.logs);
				setInvoice(response?.data?.invoice);
			} else {
				throw new Error(response?.data.message || 'Could not get payments');
			}
		} catch (error: unknown) {
			errorHandler(error);
		} finally {
			setLoading(false);
		}
	}, [orderId]);

	useEffect(() => {
		getPaymentLogs();
	}, [getPaymentLogs]);

	return (
		<InfoSlider onClose={onClose}>
			<div className={styles.main} style={{ width: '28rem' }}>
				{loading && <Loading loading={loading} />}
				<CloseIcon title="Close" className="closeBtn" onClick={onClose} />

				{!loading && (
					<div>
						<Col span={24}>
							<Row className={styles.row1}>
								<span className="title">
									{invoice.billLocation ? (
										`Capture Payment for ${invoice?.invoiceId}`
									) : (
										<>
											<h4>Handover Invoice to Salesman</h4>
											<h5>[{invoice?.invoiceId}]</h5>
										</>
									)}
								</span>
							</Row>
							<h5>
								Pending Amount <strong>₹{Number(invoice?.billAmount - invoice?.amountPaid).toFixed(2)} </strong>
							</h5>
						</Col>
						{!invoice?.isClosed ? (
							<>
								{invoice.billLocation ? (
									<PaymentForm
										orderId={orderId}
										dummyReq={dummyReq}
										getPaymentLogs={getPaymentLogs}
										onPaymentCaptured={onPaymentCaptured}
									/>
								) : (
									<SalesmanSelectForm
										setLoading={setLoading}
										salesmanId={salesmanId}
										orderId={orderId}
										getPaymentLogs={getPaymentLogs}
										onPaymentCaptured={onPaymentCaptured}
									/>
								)}
							</>
						) : (
							<h5 className={styles.closedInvoiceMessage}>Invoice status is closed no further actions allowed.</h5>
						)}
						{logs.length !== 0 && (
							<div className={styles.paymentLogContainer}>
								<Row className={styles.row1}>
									<span className="title" style={{ marginTop: '20px' }}>
										Invoice Payment History
									</span>
								</Row>
								{sortByDate({
									data: logs,
									fieldName: 'createdAt'
								}).map((log: Logs) => (
									<Timeline key={log.createdAt}>
										<PaymentLogs paymentLog={log}></PaymentLogs>
									</Timeline>
								))}
							</div>
						)}
					</div>
				)}
			</div>
		</InfoSlider>
	);
}
